import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { notificationsApi } from '../../services/apiService';
import './NotificationBell.css';

const NotificationBell = () => {
  const { isAuthenticated } = useAuth();
  const { on, off } = useSocket();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const data = await notificationsApi.getUnreadCount();
      setUnreadCount(data.count || 0);
    } catch (err) {
      // Silently fail
    }
  }, [isAuthenticated]);

  useEffect(() => { fetchUnreadCount(); }, [fetchUnreadCount]);

  // Listen for real-time notifications via WebSocket
  useEffect(() => {
    if (!on || !off) return;
    const handler = (data) => {
      setUnreadCount(prev => prev + 1);
      setNotifications(prev => [{ ...data, isRead: false }, ...prev].slice(0, 20));
    };
    on('notification', handler);
    return () => off('notification', handler);
  }, [on, off]);

  // Fetch notifications when dropdown opens
  const handleToggle = async () => {
    const next = !showDropdown;
    setShowDropdown(next);
    if (next) {
      try {
        setLoading(true);
        const data = await notificationsApi.getMy();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unreadCount || 0);
      } catch (err) {
        console.error('Failed to load notifications');
      } finally {
        setLoading(false);
      }
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDropdown]);

  // Mark single as read
  const handleMarkRead = async (id) => {
    try {
      await notificationsApi.markAsRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) { /* ignore */ }
  };

  // Mark all as read
  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) { /* ignore */ }
  };

  const formatTime = (d) => {
    if (!d) return '';
    const diff = Date.now() - new Date(d).getTime();
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  };

  const typeIcon = (type) => {
    switch (type) {
      case 'success': return '✓';
      case 'warning': return '⚠';
      default: return 'ℹ';
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="notif-bell-container" ref={dropdownRef}>
      <button className="notif-bell-btn" onClick={handleToggle} aria-label="Notifications">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {unreadCount > 0 && (
          <span className="notif-bell-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {showDropdown && (
        <div className="notif-dropdown">
          <div className="notif-dropdown-header">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <button className="notif-mark-all" onClick={handleMarkAllRead}>Mark all read</button>
            )}
          </div>

          <div className="notif-dropdown-body">
            {loading ? (
              <div className="notif-loading">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="notif-empty">No notifications yet</div>
            ) : (
              notifications.map(n => (
                <div
                  key={n.id}
                  className={`notif-item ${!n.isRead ? 'notif-unread' : ''}`}
                  onClick={() => { if (!n.isRead) handleMarkRead(n.id); }}
                >
                  <span className={`notif-icon notif-icon-${n.type || 'info'}`}>{typeIcon(n.type)}</span>
                  <div className="notif-item-content">
                    <span className="notif-item-title">{n.title}</span>
                    <span className="notif-item-message">{n.message?.length > 80 ? n.message.substring(0, 80) + '...' : n.message}</span>
                    <span className="notif-item-time">{formatTime(n.createdAt)}</span>
                  </div>
                  {!n.isRead && <span className="notif-unread-dot" />}
                </div>
              ))
            )}
          </div>

          <div className="notif-dropdown-footer">
            <button onClick={() => { setShowDropdown(false); navigate('/notifications'); }}>View All</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
